import { DateTime } from '@ksyos/date-time';
import {
    Modal,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IPerson } from '@common/utils/getDisplayName';
import type { IDeviceStatus } from '@frontend/components/interfaces/deviceStatus';
import { DeviceStatus } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import { ListPager } from '@frontend/components/elements/ListPager';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';

interface IDevicesDetailsLogbookModalProps {
    deviceId: string;
    isOpen: boolean;
    onClose: () => void;
}

export function DevicesDetailsLogbookModal(props: IDevicesDetailsLogbookModalProps) {
    const [deviceStatuses, setDeviceStatuses] = useState<IDeviceStatus[]>();
    const [totalDeviceStatuses, setTotalDeviceStatuses] = useState<number>(0);
    const [currentLogbookPage, setCurrentLogbookPage] = useState<number>(1);
    const [logbookPageSize, setLogbookPageSize] = useState<number>(10);

    useEffect(() => {
        loadDeviceStatuses();
    }, [props.deviceId, currentLogbookPage, logbookPageSize]);

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={props.onClose}
            size="xlarge"
        >
            <Modal.Header>{translateKey('devices.logbookModal.title')}</Modal.Header>
            <Modal.CloseButton />
            <Modal.Body>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{translateKey('devices.logbookModal.date')}</TableHeader>
                                <TableHeader>{translateKey('devices.logbookModal.modifiedBy')}</TableHeader>
                                <TableHeader>{translateKey('devices.logbookModal.status')}</TableHeader>
                                <TableHeader>{translateKey('devices.logbookModal.referenceType')}</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {deviceStatuses &&
                                deviceStatuses.map((deviceStatus) => (
                                    <TableRow key={deviceStatus.id}>
                                        <TableCell>
                                            {DateTime.from(deviceStatus.created_at).format('DD-MM-YYYY')}
                                        </TableCell>
                                        <TableCell>
                                            {deviceStatus.modifierPractitioner?.nameFamily
                                                ? getDisplayName(deviceStatus.modifierPractitioner as IPerson)
                                                : '-'}
                                        </TableCell>
                                        <TableCell>{translateKey(`deviceStatusEnum.${deviceStatus.status}`)}</TableCell>
                                        <TableCell>{renderDeviceStatusData(deviceStatus)}</TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <ListPager
                    itemsPerPage={logbookPageSize}
                    onChangeItemsPerPage={(value) => {
                        setLogbookPageSize(value);
                        setCurrentLogbookPage(1);
                    }}
                    onChangePage={async (value) => setCurrentLogbookPage(value)}
                    page={currentLogbookPage}
                    total={totalDeviceStatuses}
                />
            </Modal.Body>
            <Modal.Footer />
        </Modal>
    );

    function renderDeviceStatusData(deviceStatus: IDeviceStatus) {
        switch (deviceStatus.status) {
            case DeviceStatus.issued:
                const organizationsName: string = deviceStatus
                    .deviceStatusOrganizations!.map(
                        (deviceStatusOrganization) => deviceStatusOrganization.organization.name
                    )
                    .sort()
                    .join(', ');

                return (
                    <>
                        {organizationsName.length > 50 ? (
                            <Popover content={organizationsName}>
                                <span>{organizationsName.substring(0, 50)}...</span>
                            </Popover>
                        ) : (
                            <span>{organizationsName}</span>
                        )}
                    </>
                );
            case DeviceStatus.internalUse:
                return getDisplayName(deviceStatus.practitioner as IPerson);
            case DeviceStatus.homeExamination:
            case DeviceStatus.sentForRepair:
                return deviceStatus.device.deviceType.manufacturer ?? '-';
            default:
                return deviceStatus.reference ?? '-';
        }
    }

    async function loadDeviceStatuses() {
        const url = `/api/deviceStatuses/${props.deviceId}?limit=${logbookPageSize}&page=${currentLogbookPage}`;
        const result = await fetchJson(url, 'GET');
        setDeviceStatuses(result.json.deviceStatuses);
        setTotalDeviceStatuses(result.json.total);
    }
}
